import createReducer from '../utils/createReducer'
import * as actions from './login.actions'
import {readURLArguments, cleanURL} from '../components/login/helpers/readUrlArguments.helper'
import {codeSendDefaultDelay, twoFactorCodePrefix, loginModes} from '../components/login/helpers/login.data'
import {doesCookieExist, isModeOneOf} from '../components/login/login.helper'

const {createNewPassword, renewExpiredPassword, tfaCode, normal, audit, newUserLinkExpired, forgottenPasswordLinkExpired} = loginModes
const {state: initialAppState, url, failedTimes, username, firstTime, tfa, tfachannel: TFAChannel} = readURLArguments(window.location.search)

const hasTFACookie = doesCookieExist('ag-tfa')
cleanURL()

if (url && window.localStorage) {
  window.localStorage.setItem('loginRedirectURL', url)
}

const loginReducer = createReducer({
  initialState: {
    mode: isModeOneOf({mode: initialAppState, modes: [createNewPassword, renewExpiredPassword, tfaCode, audit, newUserLinkExpired, forgottenPasswordLinkExpired]}) ? initialAppState : normal,
    username: username || '',
    isUsernameDirty: username !== undefined,
    password: '',
    isPasswordDirty: false,
    confirmPassword: '',
    isConfirmPasswordDirty: false,
    secretCode: twoFactorCodePrefix,
    isSecretCodeDirty: false,
    isSecretCodeSending: false,
    isAuthInProgress: false,
    codeSendDelay: 0,
    codeSendMessage: '',
    wasExistingPasswordUsed: false,
    hasAuthFailed: failedTimes !== undefined,
    authFailedTimes: failedTimes ? parseInt(failedTimes, 10) : 0,
    isTFAEnabled: tfa !== 'false',
    TFAChannel,
    hasTFACookie,
    passwordPolicy: {},
    ssoInformation: {
      ssoEnabled: false,
      ssoClients: {
        clientRegistrations: [
          {
            registrationId: 'hydra',
            clientId: 'c8131bbf-e60a-41e1-a643-50a7d6232bed',
            fromOidcIssuerLocation: 'http://127.0.0.1:4444'
          }
        ]
      }
    },
    isPasswordPolicyPending: false,
    isInstanceAvailable: true,
    instanceProperties: {
      name: '',
      type: '',
      troubleshootingInfo: ''
    },
    isFirstTime: (firstTime === 'true'),
    canRequestNewCode: false,
    auditMessage: '',
    isAuditMessageDirty: false,
    auditSubmitFailed: false,
    isMobile: false
  },
  actions: {
    [actions.switchLoginMode]: ({action}) => {
      if ([createNewPassword, renewExpiredPassword].includes(action.mode)) {
        return {mode: action.mode, isAuthInProgress: false, hasAuthFailed: false, authFailedTimes: 0, password: '', isPasswordDirty: false}
      }
      return {mode: action.mode, isAuthInProgress: false, hasAuthFailed: false, authFailedTimes: 0}
    },
    [actions.updateUsername]: ({action}) => ({username: action.value, isUsernameDirty: true}),
    [actions.setUsernameDirty]: () => ({isUsernameDirty: true}),
    [actions.updatePassword]: ({action}) => ({password: action.value, wasExistingPasswordUsed: false}),
    [actions.setPasswordDirty]: () => ({isPasswordDirty: true}),
    [actions.updateConfirmPassword]: ({action}) => ({confirmPassword: action.value, wasExistingPasswordUsed: false}),
    [actions.setConfirmPasswordDirty]: () => ({isConfirmPasswordDirty: true}),
    [actions.setAuthInProgress]: ({action}) => ({isAuthInProgress: action.isInProgress, hasAuthFailed: false}),
    [actions.setAuthFailed]: ({action, state}) => ({isAuthInProgress: false, hasAuthFailed: action.errorMessage, authFailedTimes: state.authFailedTimes + 1}),
    [actions.setAuditSubmitFailed]: () => ({auditSubmitFailed: true}),
    [actions.updateSecretCode]: ({action}) => {
      if (`${twoFactorCodePrefix}${action.value.replace(/\D/g, '')}` === action.value) {
        return ({secretCode: action.value, isSecretCodeDirty: true})
      }
      if (action.value.length <= 5) {
        return ({secretCode: twoFactorCodePrefix, isSecretCodeDirty: true})
      }
      return {}
    },
    [actions.setSecretCodeDirty]: () => ({isSecretCodeDirty: true}),
    [actions.setSecretCodeSending]: ({action}) => ({isSecretCodeSending: action.sending, codeSendDelay: codeSendDefaultDelay}),
    [actions.tickCodeDelayTimer]: ({action}) => ({codeSendDelay: action.value}),
    [actions.resetSecretCodeSendingDelay]: () => ({codeSendDelay: 0}),
    [actions.codeSendFailed]: ({action}) => ({codeSendMessage: action.message}),
    [actions.setExistingPasswordUsed]: ({action}) => ({wasExistingPasswordUsed: action.wasExistingPasswordUsed}),
    [actions.setPasswordPolicy]: ({action}) => ({passwordPolicy: action.passwordPolicy, isPasswordPolicyPending: false}),
    [actions.setSsoInformation]: ({action}) => ({ssoInformation: action.ssoInformation}),
    [actions.setPasswordPolicyPending]: ({action}) => ({isPasswordPolicyPending: action.pending}),
    [actions.setInstanceAvailable]: ({action}) => ({isInstanceAvailable: action.available}),
    [actions.setInstanceProperties]: ({action}) => ({instanceProperties: action.properties}),
    [actions.enableRequestingNewCode]: () => ({canRequestNewCode: true, codeSendDelay: 0}),
    [actions.updateAuditMessage]: ({action: {auditMessage}}) => ({auditMessage}),
    [actions.setAuditMessageDirty]: () => ({isAuditMessageDirty: true}),
    [actions.onUpdateView]: ({action: {isMobile}}) => ({isMobile})
  },
  options: {
    mode: 'setState'
  }
})

export default loginReducer
